import { ActionResult } from '../enums';
import type { ActionHandler } from '../types';
import { showAlertModal } from '~/utils/alert-modal.ts';
import { isRecord } from '~/services/layout/layout.ts';

export type ConfirmDialogOptions = {
  title?: string;
  message?: string;
  okButtonText?: string;
  cancelButtonText?: string;
};

type ConfirmPlaylistDialog = (
  options: Required<ConfirmDialogOptions>
) => Promise<boolean>;

/**
 * Reads a playlist-popup confirm callback from action context when present.
 */
function getConfirmPlaylistDialog(
  context: unknown
): ConfirmPlaylistDialog | undefined {
  if (!isRecord(context)) {
    return undefined;
  }

  const confirmPlaylistDialog = context.confirmPlaylistDialog;

  return typeof confirmPlaylistDialog === 'function'
    ? (confirmPlaylistDialog as ConfirmPlaylistDialog)
    : undefined;
}

/**
 * Shows a confirmation dialog and cancels the action chain when declined.
 * Playlist popup context uses its own dialog; otherwise the Theme Alert Modal is used.
 */
export const confirmDialogHandler: ActionHandler<ConfirmDialogOptions> = async (
  action
) => {
  const options = action.options ?? {};
  const title = options.title ?? 'Confirm';
  const message = options.message ?? 'Are you sure?';
  const okButtonText = options.okButtonText ?? 'OK';
  const cancelButtonText = options.cancelButtonText ?? 'Cancel';

  const confirmPlaylistDialog = getConfirmPlaylistDialog(action.context);

  if (confirmPlaylistDialog) {
    const confirmed = await confirmPlaylistDialog({
      title,
      message,
      okButtonText,
      cancelButtonText,
    });

    return confirmed ? ActionResult.Success : ActionResult.Cancel;
  }

  const confirmed = await showAlertModal({
    title,
    message,
    primaryButtonLabel: okButtonText,
    secondaryButtonLabel: cancelButtonText,
  });

  if (confirmed === null) {
    return ActionResult.Error;
  }

  return confirmed ? ActionResult.Success : ActionResult.Cancel;
};
